import { PhoneIncoming, PhoneMissed, PhoneOutgoing, type LucideIcon } from "lucide-react";
import * as React from "react";
import { leadName } from "@/lib/campaign";
import {
  callerContact, callerSearchDigits, callsBeforeText, colleagueFor, legResult, legText,
} from "@/lib/phone-page";
import { useCalls, useCallerContacts, useColleagues, useLeadCard } from "@/lib/queries";
import { OWN_NON_CALL_LEGS, type Cdr, type LeadCard } from "@/lib/types";
import { cn, formatDateTime, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { useAcdCall } from "@/softphone/acd-call";
import { describeParty } from "@/softphone/call-views";
import { LeadCardView } from "@/softphone/lead-card";
import { useSoftphone, type Call } from "@/softphone/softphone-context";

/** The caller's earlier calls listed on their card; the rest are in My calls. */
const EARLIER_ON_THE_CARD = 3;

/**
 * How long a call is given for the rep's record to say the ACD rang them for it, before the
 * contacts are asked who is calling. The switch's invite usually reaches the browser before the
 * record does (`useCampaignAutoAnswer` in `softphone/acd-call.tsx` says the same), and an ACD call
 * is the ACD's to name: without the wait, every one of them asked the contacts as it rang.
 */
export const RECORD_GRACE_MS = 1_500;

// One clock for each call, shared by every surface that asks (the ringing stage, the call bar, the
// card), so a surface drawn later does not start the wait again. A few dozen calls are kept.
const firstSeen = new Map<string, number>();
const SEEN_KEPT = 50;

function seenAt(callId: string): number {
  const at = firstSeen.get(callId);
  if (at !== undefined) return at;
  if (firstSeen.size >= SEEN_KEPT) {
    const oldest = firstSeen.keys().next().value;
    if (oldest !== undefined) firstSeen.delete(oldest);
  }
  const now = Date.now();
  firstSeen.set(callId, now);
  return now;
}

/** Forgets every call's clock. For tests. */
export function resetCallerLookups(): void {
  firstSeen.clear();
}

/** Whether `callId` has been on the phone long enough for the rep's record to have named it. */
function useGraceOver(callId: string | null): boolean {
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  const left = callId === null ? 0 : seenAt(callId) + RECORD_GRACE_MS - Date.now();
  const waiting = left > 0;
  React.useEffect(() => {
    if (!waiting) return;
    const timer = setTimeout(tick, left);
    return () => clearTimeout(timer);
    // `left` shrinks on every render; the timer is for the moment it runs out.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callId, waiting]);
  return callId !== null && !waiting;
}

/** Who is calling, as far as the contacts say (`useCallerLookup`). */
export interface CallerLookup {
  /** The search or the card is still on its way. The ringing stage keeps the number on screen
   *  until there is an answer; the card under a call waits, so it does not show a stand-in first. */
  pending: boolean;
  /** The caller's lead card, read. */
  card: LeadCard | null;
  /** The campaign the card counts the lead's attempts on and words their outcomes with. */
  campaignId: string | null;
  /** The search answered, and the number is none of the contacts this person may open. */
  unknown: boolean;
  /** The rep's record may still say the ACD rang them for this call (`RECORD_GRACE_MS`): nothing
   *  is asked yet. The ringing stage shows the number meanwhile; the page's card waits. */
  waiting: boolean;
}

const NOBODY: CallerLookup = {
  pending: false, card: null, campaignId: null, unknown: false, waiting: false,
};

/**
 * Who is on a call the ACD did not bring (ACL-483): a call to the rep's own number, or one they
 * placed from the phone. The ACD looks a caller up before it rings anybody (ADR-023), and the
 * dialer brings its lead with the call; nothing does that for these, so the page did not know a
 * contact who called the rep's own number from one who never had. The console asks the contacts
 * search for the number, as a rep typing it into Contacts would, and reads the card of the newest
 * contact with exactly that number (`callerContact`, the ACD's own rule).
 *
 * The search is the contacts list's route and scope, so a rep finds only the contacts they may open
 * (their campaigns' lists, the people they have spoken to): anybody else is "Not in your
 * contacts", which is what it says, rather than a record the rep could not open anyway. It is
 * asked once for a call (`useCallerContacts`), not again on the list's poll or on every contact
 * written on the floor.
 *
 * Nothing for the ACD's own calls, whose record says who the caller is, nor for an internal
 * transfer, whose number is the other system's line and not the client's (`isInternalTransfer`),
 * nor for anything that is not an outside number. A new call is given a moment for the record to
 * claim it first (`RECORD_GRACE_MS`). `enabled` false asks nothing, for a page that already has
 * the lead.
 */
export function useCallerLookup(call: Call | null, enabled = true): CallerLookup {
  const acd = useAcdCall();
  const theAcds = call !== null && acd !== null && acd.callId === call.id;
  const outside =
    enabled && call !== null && !theAcds && call.internalTransfer !== true &&
    callerSearchDigits(call.remote) !== null;
  const graceOver = useGraceOver(outside ? call.id : null);
  const digits = outside && graceOver ? callerSearchDigits(call.remote) : null;
  const search = useCallerContacts(digits);
  const answered = digits !== null && search.data !== undefined;
  const contact = answered && call !== null ? callerContact(search.data?.items, call.remote) : null;
  const read = useLeadCard(contact?.id ?? null, null);
  if (outside && !graceOver) return { ...NOBODY, waiting: true };
  if (digits === null) return NOBODY;
  if (!answered) return search.isError ? NOBODY : { ...NOBODY, pending: true };
  if (contact === null) return { ...NOBODY, unknown: true };
  const card = read.data !== undefined && read.data.id === contact.id ? read.data : null;
  // A card that could not be read leaves the caller as a number, not a blank.
  if (card === null) return read.isError ? NOBODY : { ...NOBODY, pending: true };
  return { ...NOBODY, card, campaignId: contact.membership?.campaign_id ?? null };
}

/**
 * How the ringing stage and the call bar name the party (ACL-483): by the contact's name over
 * their number once the contacts know the caller, and otherwise as `describeParty` has it. One
 * hook for both, so the two cannot name a caller differently. `enabled` false keeps
 * `describeParty`'s words and asks nothing.
 */
export function useCallerTitle(
  call: Call,
  party: { title: string; subtitle: string },
  enabled = true,
): { lookup: CallerLookup; title: string; subtitle: string } {
  const lookup = useCallerLookup(call, enabled);
  const name = lookup.card ? leadName(lookup.card) : null;
  return name
    ? { lookup, title: name, subtitle: formatPhoneNumber(call.remote) }
    : { lookup, title: party.title, subtitle: party.subtitle };
}

/**
 * The card under a call the page has no lead for (ACL-483): the caller's lead card when the
 * number is one of our contacts, drawn as the dialer's lead's card is, and otherwise who this
 * is in the same place and the same shape (`CallerFacts`).
 *
 * It replaced a panel that also carried a colleague picker (Warm transfer, Conference, Blind
 * transfer) on a call to the rep's own number. That picker is the Transfer dialog's own
 * (`TransferDialog`), one press away in the controls with Add participant beside it, so the page
 * gives the space to who the rep is talking to, as it does on every other call.
 */
export function CallerCard({ call, mine }: { call: Call; mine: readonly string[] }) {
  const acd = useAcdCall();
  const lookup = useCallerLookup(call);
  // The ACD's call that names a lead: its card is drawn already (`LeadCardPanel`).
  if (acd !== null && acd.callId === call.id && acd.leadId !== null) return null;
  if (lookup.pending || lookup.waiting) return null;
  if (lookup.card !== null) {
    return <LeadCardView card={lookup.card} campaignId={lookup.campaignId} full frame={false} />;
  }
  return <CallerFacts call={call} mine={mine} unknown={lookup.unknown} />;
}

/**
 * Who a caller with no lead card is, drawn as the card is: their name or number with what they
 * are to the rep on the right (a colleague, an outside number, not in your contacts, your room),
 * then, for an outside number, the rep's earlier calls with it, each as a row as the card has
 * them, and "First call with you" when there are none. The number is the call bar's, under the
 * name there, and the card does not say it again, as the lead card does not.
 */
function CallerFacts({ call, mine, unknown }: { call: Call; mine: readonly string[]; unknown: boolean }) {
  const phone = useSoftphone();
  const colleagues = useColleagues();
  const colleague = colleagueFor(call.remote, colleagues.data);
  const outside = colleague === null && callerSearchDigits(call.remote) !== null;
  const history = useCalls({ mine: true, exclude_leg: OWN_NON_CALL_LEGS, q: call.remote }, 0, {
    enabled: outside,
  });
  const { title, isRoom } = describeParty(call, phone.conference);
  const badge = colleague
    ? `Colleague · ext. ${colleague.extension}`
    : unknown
      ? "Not in your contacts"
      : outside
        ? "Outside number"
        : isRoom
          ? "Your room"
          : null;
  // Another call's rows stand in while this number's are read: they are not this caller's.
  const legs = history.isPlaceholderData ? null : (history.data?.items ?? null);

  return (
    <section aria-label="The caller" className="w-full px-1 text-left text-xs">
      <div className="flex items-start justify-between gap-2">
        <h3 className="min-w-0 truncate text-sm font-semibold text-text">{title}</h3>
        {badge && (
          <span className="shrink-0 rounded-md bg-gray-3 px-1.5 py-0.5 text-2xs font-medium text-gray-11">
            {badge}
          </span>
        )}
      </div>
      {outside && legs !== null && (
        legs.length === 0 ? (
          <p className="mt-1 text-text-muted">First call with you.</p>
        ) : (
          <EarlierLegs legs={legs.slice(0, EARLIER_ON_THE_CARD)} total={history.data?.total ?? legs.length} mine={mine} />
        )
      )}
    </section>
  );
}

/** A leg's mark, by how it went: the lead card's tones (`softphone/lead-card.tsx`). */
const LEG_MARKS: Record<ReturnType<typeof legResult>, { icon: LucideIcon; tone: string }> = {
  talked_in: { icon: PhoneIncoming, tone: "bg-success-2 text-success-11" },
  talked_out: { icon: PhoneOutgoing, tone: "bg-success-2 text-success-11" },
  missed: { icon: PhoneMissed, tone: "bg-warning-2 text-warning-11" },
  no_answer: { icon: PhoneMissed, tone: "bg-gray-3 text-gray-11" },
};

/** The rep's earlier calls with this number, newest first, as the lead card's Previous calls. */
function EarlierLegs({ legs, total, mine }: { legs: Cdr[]; total: number; mine: readonly string[] }) {
  const summary = callsBeforeText(total);
  return (
    <div className="mt-2 border-t border-border pt-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="shrink-0 text-2xs font-semibold uppercase tracking-wide text-text-subtle">
          Previous calls
        </h3>
        {summary && <span className="min-w-0 truncate text-2xs text-text-muted">{summary}</span>}
      </div>
      <ol className="mt-1.5">
        {legs.map((leg, i) => {
          const mark = LEG_MARKS[legResult(leg, mine)];
          return (
            <li key={leg.id} className="relative flex gap-2 pb-2.5 last:pb-0">
              {i < legs.length - 1 && (
                <span aria-hidden className="absolute bottom-0 left-3 top-6 w-px -translate-x-1/2 bg-border" />
              )}
              <span className={cn("flex size-6 shrink-0 items-center justify-center rounded-full", mark.tone)}>
                <mark.icon className="size-3" aria-hidden />
              </span>
              <p className="flex min-w-0 flex-1 items-baseline gap-2 pt-1">
                <span className="min-w-0 flex-1 text-text">{legText(leg, mine)}</span>
                {leg.start_time && (
                  <time
                    dateTime={leg.start_time}
                    title={formatDateTime(leg.start_time)}
                    className="shrink-0 text-2xs text-text-subtle"
                  >
                    {relativeTime(leg.start_time)}
                  </time>
                )}
              </p>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
